import React, { useState, useEffect } from 'react';
import { updateReservation, createReservation, sendReservationConfirmedNotification } from '../../services/apiClient';
import logger from '../../utils/logger';
import { Modal, Button } from '../ui';
import { Input, Select, Textarea } from '../ui/Field';

const allRestaurants = [
  'Inova Foz - Vila do Conde',
  'Inova Regio - Vila do Conde',
  'Inova Trindade - Porto',
  'Inova Beach - Azurara',
];

const allStatuses = [
  { value: 'pending', label: 'Pendente' },
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'completed', label: 'Concluído' },
  { value: 'canceled', label: 'Cancelado' },
];

const ReservationModal = ({ isOpen, onClose, reservation, apiUrl, token, onSuccess, mode = 'edit' }) => {
  const [formData, setFormData] = useState({
    state: 'pending',
    branch: allRestaurants[0],
    name: '',
    phone: '',
    email: '',
    booking_date: '',
    from_time: '',
    to_time: '',
    total_guest: 1,
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (reservation && mode === 'edit') {
      setFormData({
        state: reservation.state || 'pending',
        branch: reservation.branch || allRestaurants[0],
        name: reservation.name || '',
        phone: reservation.phone || '',
        email: reservation.email || '',
        booking_date: reservation.booking_date || '',
        from_time: reservation.from_time || '',
        to_time: reservation.to_time || '',
        total_guest: reservation.total_guest || 1,
        message: reservation.message || '',
      });
    } else if (mode === 'create') {
      setFormData({
        state: 'pending',
        branch: allRestaurants[0],
        name: '',
        phone: '',
        email: '',
        booking_date: '',
        from_time: '',
        to_time: '',
        total_guest: 1,
        message: '',
      });
    }
    setError('');
  }, [reservation, mode, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    logger.userAction(mode === 'edit' ? 'Update reservation' : 'Create reservation', {
      reservationId: reservation?.id,
      formData,
    });

    try {
      let data;
      const wasConfirmed = reservation?.state === 'confirmed';
      const isNowConfirmed = formData.state === 'confirmed';

      if (mode === 'edit') {
        data = await updateReservation(apiUrl, token, reservation.id, formData);
      } else {
        data = await createReservation(apiUrl, token, formData);
      }

      if (data.status === 'success' && data.reservation) {
        logger.info(`Reservation ${mode === 'edit' ? 'updated' : 'created'} successfully`, {
          reservationId: data.reservation.id,
        });

        // Send WhatsApp notification if status changed to confirmed
        if (mode === 'edit' && !wasConfirmed && isNowConfirmed) {
          logger.info('Sending WhatsApp confirmation', { reservationId: data.reservation.id });
          try {
            await sendReservationConfirmedNotification(apiUrl, token, {
              to_number: data.reservation.phone,
              template_vars: {
                '1': data.reservation.name,
                '2': data.reservation.branch,
                '3': data.reservation.booking_date,
                '4': data.reservation.from_time,
                '5': String(data.reservation.total_guest),
              },
            });
            logger.info('WhatsApp confirmation sent successfully');
          } catch (notifError) {
            logger.error('Failed to send WhatsApp notification', { error: notifError.message });
          }
        }

        onSuccess(data.reservation, mode);
        onClose();
      } else {
        logger.error(`Failed to ${mode} reservation - unexpected response`, { data });
        setError(data.message || 'Erro ao processar a reserva.');
      }
    } catch (err) {
      logger.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} reservation`, {
        error: err.message,
      });
      setError(err.message || 'Erro de comunicação com o servidor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="md"
      title={mode === 'edit' ? 'Editar Reserva' : 'Criar Nova Reserva'}
      subtitle={mode === 'edit' ? 'Atualize os detalhes da reserva.' : 'Preencha os dados da nova reserva.'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-xl bg-status-canceled-soft text-status-canceled px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <Select
          label="Estado da Reserva"
          id="state"
          name="state"
          value={formData.state}
          onChange={handleChange}
        >
          {allStatuses.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </Select>

        <Select
          label="Restaurante"
          id="branch"
          name="branch"
          value={formData.branch}
          onChange={handleChange}
        >
          {allRestaurants.map((restaurant) => (
            <option key={restaurant} value={restaurant}>
              {restaurant}
            </option>
          ))}
        </Select>

        <Input
          label="Nome do Cliente"
          required
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
        />

        <Input
          label="Telefone"
          type="text"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
        />

        <Input
          label="Email"
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
        />

        <Input
          label="Data"
          required
          type="date"
          id="booking_date"
          name="booking_date"
          value={formData.booking_date}
          onChange={handleChange}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Hora de Início"
            required
            type="time"
            id="from_time"
            name="from_time"
            value={formData.from_time}
            onChange={handleChange}
          />
          <Input
            label="Hora de Fim"
            type="time"
            id="to_time"
            name="to_time"
            value={formData.to_time}
            onChange={handleChange}
          />
        </div>

        <Input
          label="Pessoas"
          required
          type="number"
          id="total_guest"
          name="total_guest"
          value={formData.total_guest}
          onChange={handleChange}
          min="1"
        />

        <Textarea
          label="Mensagem"
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="3"
        />

        <div className="flex gap-3 pt-2">
          <Button type="submit" loading={loading} className="flex-1">
            {loading ? 'A Processar...' : mode === 'edit' ? 'Salvar Alterações' : 'Criar Reserva'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ReservationModal;
